import React, { useEffect, useState, useRef } from 'react';
import './app.css';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fcmIsSupported, fetchToken, onMessageListener } from "./fcm/firebase";
import growl from "./lib/growl/growl";
import {
    checkTokenSagaAction,
    currentVisitorsSagaAction,
    establishmentListSagaAction,
    getGiftsOrdersSagaAction,
    getSubscriptionDataSagaAction,
    getTasksSagaAction,
    saveWebTokenSagaAction
} from "./redux/sagaActions";
import {
    currentEstablishmentAction,
    dropdownAction,
    filterWindowAction,
    firstCurrentVisitorsRequestAction,
    messagesAction,
    monitorViewAction,
    selectAction,
    taskArchiveAction
} from "./redux/rootReducer";
import Preloader from "./components/Preloader/Preloader";
import Auth from "./pages/Auth/Auth";
import Sidebar from "./components/Sidebar/Sidebar";
import Monitor from "./pages/Monitor/Monitor";
import Users from "./pages/Users/Users";
import Subscriptions from "./pages/Subscriptions/Subscriptions";
import Statistic from "./pages/Statistic/Statistic";
import Settings from "./pages/Settings/Settings";
import Account from "./pages/Account/Account";
import QR from "./pages/QR/QR";
import { getLocalStorage, setLocalStorage } from "./helpers/LocalStorage";
import Notifications from "./pages/Notifications/Notifications";
import Promo from "./pages/Promo/Promo";
import Price from "./pages/Price/Price";
import Workouts from "./pages/Workouts/Workouts";
import Coaches from "./pages/Coaches/Coaches";
import Sales from "./pages/Sales/Sales";
import Booking from "./pages/Booking/Booking";
import Gifts from "./pages/Gifts/Gifts";
import Goods from "./pages/Goods/Goods";
import Tasks from "./pages/Tasks/Tasks";
import { SELECT_INITIAL } from "./redux/initialStateConstant";
import Msg from "./components/Msg/Msg";
import { CSSTransition } from "react-transition-group";
import Automation from "./pages/Automation/Automation";
import Admins from "./pages/Admins/Admins";
import Improvements from "./pages/Improvements/Improvements";
import Salary from "./pages/Salary/Salary";
import Leaderboard from "./pages/Leaderboard/Leaderboard";
import Reviews from "./pages/Reviews/Reviews";
import Counterparties from "./pages/Counterparties/Counterparties";
import Cash from "./pages/Cash/Cash";
import Invoices from "./pages/Invoices/Invoices";
import Slugs from "./pages/Slugs/Slugs";
import Services from "./pages/Services/Services";

const App = () => {
    const dispatch = useDispatch();
    const startApp = useSelector(state => state.startApp);
    const loader = useSelector(state => state.loader);
    const user = useSelector(state => state.user);
    const establishmentList = useSelector(state => state.establishmentList);
    const currentEstablishment = useSelector(state => state.currentEstablishment);
    const dropdown = useSelector(state => state.dropdown);
    const debt = useSelector(state => state.debt);
    const filterWindow = useSelector(state => state.filterWindow);
    const filterUser = useSelector(state => state.filterUser);
    const giftsStatusFilterOptions = useSelector(state => state.giftsStatusFilterOptions);
    const messages = useSelector(state => state.messages);

    const [msgIncrement, setMsgIncrement] = useState(0);

    const currentEst = useRef({});

    useEffect(() => {
        if (!establishmentList.length) return;
        // Получение текущего заведения из localStorage
        getLocalStorage('afp_est').then(data => {

            // Проверяем наличие id, хранящегося в базе данных в списке заведений
            if (data && establishmentList.some(el => el.id === data)) {
                dispatch(currentEstablishmentAction(data));
            }
            else {
                dispatch(currentEstablishmentAction(establishmentList[0].id));
                setLocalStorage('afp_est', establishmentList[0].id).then();
            }
        });
    }, [establishmentList]);

    useEffect(() => {
        // Получение списка заведений
        if (!user.id || !user.hash) return;
        dispatch(establishmentListSagaAction());
    }, [user.id]);

    useEffect(() => {
        // Проверка токена
        dispatch(checkTokenSagaAction());
    }, [dispatch]);

    // Получение данных о тренерах и абонементах (типы, продолжительность и т.д.)
    useEffect(() => {
        if (!currentEstablishment) return;

        dispatch(getSubscriptionDataSagaAction());
        dispatch(getTasksSagaAction());
    }, [currentEstablishment]);

    // Отрисовка адреса в зависимости от количества заведений (адресов)
    useEffect(() => {

        if (!currentEstablishment) return;

        if (establishmentList.length > 1) {
            document.querySelector('.layout').classList.add('layout--address');
            currentEst.current = establishmentList.find(el => el.id === currentEstablishment);
        }

    }, [establishmentList, currentEstablishment]);

    // Сохраняем web_token
    useEffect(() => {
        fcmIsSupported().then(fcmSupported => {
            if (!fcmSupported) return;

            if (!user.id || !user.hash) return;
            if (Notification.permission !== 'granted') return;

            // Получение пуш токена и его сохранение
            fetchToken().then(token => {
                if (token && token !== user.web_token) {
                    dispatch(saveWebTokenSagaAction({ web_token: token }));
                }
            });
        });
    }, [user.id]);

    // Вешаем обработчик отображения сообщений
    useEffect(() => {
        if (!currentEstablishment) return;

        fcmIsSupported().then(fcmSupported => {
            if (!fcmSupported) return;

            if (Notification.permission !== 'granted') {
                // Запрашиваем права на отправку уведомлений
                Notification.requestPermission()
                    .then(() => {
                    });
                window.alert('Разрешите получать уведомления от сайта, чтобы ничего не пропустить');
                return;
            }

            onMessageListener().then(payload => {
                setMsgIncrement(msgIncrement + 1);
                const notificationOption = {
                    body: payload.notification.body,
                    icon: payload.notification.icon
                };

                if (Notification.permission === "granted") {
                    let notification = new Notification(payload.notification.title, notificationOption);

                    notification.onclick = function (ev) {
                        ev.preventDefault();
                        window.open(payload.notification.click_action, '_blank');
                        notification.close();
                    }
                }
                // Пришёл новый пользователь
                if (payload.data.type === 'new_user') {
                    // Получаем список посетителей в зале, если находимся в мониторинге
                    if (window.location?.pathname === '/fitness') {
                        dispatch(currentVisitorsSagaAction({ establishment: currentEstablishment }));
                    }
                    else if ((user.settings?.new_user === '0' || user.settings?.new_user === undefined)){
                        growl({ type: 'success', message: payload.notification.title + ': ' + payload.notification.body });
                    }
                }
                // Запись на групповое занятие
                else if (payload.data.type === 'new_booking') {

                    if ((user.settings?.new_booking === '0' || user.settings?.new_booking === undefined)) {
                        growl({ type: 'success', message: payload.notification.title + ': ' + payload.notification.body });
                    }
                }
                // Задача
                else if (payload.data.type === 'new_task') {
                    // Если открыт архив задач, переходим в текущие задачи
                    dispatch(taskArchiveAction(false));
                    dispatch(getTasksSagaAction());
                }
                // Заказ бонуса
                else if (payload.data.type === 'new_order_gift') {
                    if (window.location.pathname === '/fitness/gifts') {
                        // Запрашиваем заказы бонусов
                        dispatch(getGiftsOrdersSagaAction({
                            establishment: currentEstablishment,
                            page: 1,
                            user: filterUser.user.id,
                            status: JSON.stringify(giftsStatusFilterOptions.filter(el => el.active).map(el => el.id))
                        }));
                    }
                    growl({ type: 'success', message: payload.notification.title });
                }
                // Я пришёл
                else if (payload.data.type === 'i_came') {
                    let user = JSON.parse(payload.data.user);

                    let newMsg = [...messages];

                    let checkUserExist = false;

                    newMsg.forEach(elem => {

                        if (elem.id === user?.id) {
                            checkUserExist = true;
                        }
                    });

                    if (!checkUserExist) {
                        newMsg.push(
                            { id: user?.id, name: user?.name, img: user?.img, nodeRef: Object.create({ current: null }) }
                        );

                        dispatch(messagesAction(newMsg));
                    }
                }
            });
        });
    }, [currentEstablishment, msgIncrement, messages]);

    // Обработчик клика
    const clickHandler = event => {
        // Если клик произошёл вне селекта, закрываем его
        if (!event.target.closest('.slt')) {
            dispatch(selectAction(SELECT_INITIAL));
        }

        // Если клик произошёл вне дропдауна, закрываем его
        if ((!event.target.closest('.dropdown') && !event.target.closest('.select__option')) && dropdown) {
            dispatch(dropdownAction(null));
        }

        // Если клик произошёл вне фильтров, закрываем их
        if (
            filterWindow && !event.target.closest('.flt')
            && !event.target.closest('.filter-calendar')
            && !event.target.closest('.select__remove')
            && !event.target.closest('.filter__remove')
            && !event.target.closest('.calend')
        ) {
            dispatch(filterWindowAction(false));
        }
    };

    useEffect(() => {
        document.querySelector('.layout').addEventListener('click', clickHandler);

        return () => {
            document.querySelector('.layout').removeEventListener('click', clickHandler);
        };
    }, [dropdown, filterWindow]);

    // Получение из локального хранилища типа отображения монитора и типа отображения расписания
    useEffect(() => {
        getLocalStorage('afp_monitor_view').then(data => {

            if (data) {
                dispatch(monitorViewAction(data));
            }
            else {
                dispatch(monitorViewAction('tile'));
            }
        });
    }, []);

    if (!startApp) {
        return <Preloader/>
    }

    if ((!user || !user.id)) {
        return <Auth/>
    }

    const routes = [
        { path: '/fitness', Component: Monitor },
        { path: '/fitness/users/:id?', Component: Users },
        { path: '/fitness/subscriptions', Component: Subscriptions },
        { path: '/fitness/workouts', Component: Workouts },
        { path: '/fitness/booking', Component: Booking },
        { path: '/fitness/statistic', Component: Statistic },
        { path: '/fitness/settings', Component: Settings },
        { path: '/fitness/account', Component: Account },
        { path: '/fitness/notifications', Component: Notifications },
        { path: '/fitness/promo', Component: Promo },
        { path: '/fitness/qr', Component: QR },
        { path: '/fitness/price', Component: Price },
        { path: '/fitness/coaches', Component: Coaches },
        { path: '/fitness/salary', Component: Salary },
        { path: '/fitness/sales', Component: Sales },
        { path: '/fitness/gifts', Component: Gifts },
        { path: '/fitness/goods', Component: Goods },
        { path: '/fitness/tasks', Component: Tasks },
        { path: '/fitness/automation', Component: Automation },
        { path: '/fitness/admins', Component: Admins },
        { path: '/fitness/improvements', Component: Improvements },
        { path: '/fitness/leaderboard', Component: Leaderboard },
        { path: '/fitness/reviews', Component: Reviews },
        { path: '/fitness/invoices', Component: Counterparties },
        { path: '/fitness/cash', Component: Cash },
        { path: '/fitness/payments', Component: Invoices },
        { path: '/fitness/slugs', Component: Slugs },
        { path: '/fitness/services', Component: Services }
    ];

    return (
        <>
            <div className="address"
                onClick={ () => dispatch(dropdownAction('choice_est_global')) }
            >
                { currentEst.current?.address || '' }
                <div className={ `dropdown__items dropdown__items--establishment ${ dropdown === 'choice_est_global' ? 'dropdown__items--open' : '' }` }>
                    {
                        establishmentList.length > 1
                            ?
                            establishmentList.map(elem => {
                                return (
                                    <div
                                        className="dropdown__item" key={ elem.id }
                                        onClick={ () => {
                                            dispatch(firstCurrentVisitorsRequestAction(false));
                                            setLocalStorage('afp_est', elem.id).then();
                                            dispatch(currentEstablishmentAction(elem.id));
                                            dispatch(dropdownAction(null));
                                        } }
                                    >{ elem.name }</div>
                                )
                            })
                            : false
                    }
                </div>
            </div>
            { user.slug === 'fitness' && user.id !== '3' && process.env.NODE_ENV === 'production' && <div className="test">Внимание! Тестовая версия</div> }
            { loader ? <Preloader/> : false }
            { debt === '1' && <div className="debt">Внимание! Во избежание блокировки, произведите оплату сервиса</div> }
            <Router>
                <Sidebar/>
                <Msg/>
                <div className="page-animation__wrapper">
                    { routes.map(({ path, Component, nodeRef={ current: null } }) =>
                        <Route key={ path } path={ path } exact>
                            {
                                ({ match }) => {
                                    return (
                                        <CSSTransition
                                            timeout={ 400 }
                                            classNames={ 'page-animation' }
                                            unmountOnExit
                                            in={ match != null }
                                            nodeRef={ nodeRef }
                                        >
                                            <div className="content" ref={ nodeRef }>
                                                <Component/>
                                            </div>
                                        </CSSTransition>
                                    );
                                }
                            }
                        </Route>
                    ) }
                </div>
            </Router>
        </>
    );
};

export default App;